import { Accordion } from '@zoopla-eng/zds-components/accordion';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button, IconButton } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { SectionTitleSmall } from '@zoopla-eng/zds-components/headings';
import { CloseMedium } from '@zoopla-eng/zds-components/icons';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Added, AddedToSite } from './AddedToSite';
import { Bedrooms, BedsMax, BedsMin } from './Bedrooms';
import { Features } from './Features';
import {
    HomeType,
    IsAuction,
    IsRetirementHome,
    IsSharedOwnership,
    NewHomes
} from './HomeType';
import { Keywords } from './Keywords';
import { Price, PriceFrequency, PriceMax, PriceMin } from './Price';
import {
    BuyerIncentive,
    ChainFree,
    IncludeSold,
    PropertyStatus,
    ReducedPrice
} from './PropertyStatus';
import { PropertySubType, PropertyType } from './PropertyType';
import { trackFilterClick } from './utils/';
import {
    getDefaultForSaleFilters,
    getForSaleFilters
} from './utils/getForSaleFilters';

const getPathname = (
    pathname: string,
    searchType: 'for-sale' | 'to-rent' | 'new-homes'
) => {
    const pathRegex = /for-sale|to-rent|new-homes/;

    return pathname.replace(pathRegex, searchType);
};

const getSearchPath = (
    propertyType: PropertyType | 'retirement' | 'bungalow',
    searchPath: string | string[]
) => {
    const searchPaths = {
        property: 'property',
        houses: 'houses',
        flats: 'flats',
        farms_land: 'property',
        retirement: 'retirement',
        bungalow: 'bungalows'
    };

    if (!searchPath || typeof searchPath === 'string') {
        return [searchPaths[propertyType]];
    }

    return [searchPaths[propertyType], ...searchPath.slice(1)];
};

type ForSaleFiltersProps = {
    onClose: () => void;
    onUpdate: () => void;
};

export const ForSaleFilters = ({ onClose, onUpdate }: ForSaleFiltersProps) => {
    const { query, push, pathname } = useRouter();

    const [isLoading, setIsLoading] = useState(false);

    const [accordionValue, updateAccordionValue] = useState<string[]>([]);

    const [formValue, updateFormValue] = useState(
        getForSaleFilters(query, pathname)
    );

    const onSubmit = async (e: { preventDefault: () => void }) => {
        setIsLoading(true);

        e.preventDefault();

        const updatedQuery = {
            ...query,
            ...formValue,
            'search-path': getSearchPath(
                formValue['property_type'] as PropertyType,
                query['search-path'] ?? ''
            )
        };

        const updatedPathname = getPathname(
            pathname,
            formValue['new_homes'] === 'only' ? 'new-homes' : 'for-sale'
        );

        if (
            formValue['is_retirement_home'] === 'true' &&
            formValue['property_type'] === 'property'
        ) {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'is_retirement_home'
            ];

            updatedQuery['search-path'] = getSearchPath(
                'retirement',
                updatedQuery['search-path']
            );
        }

        if (formValue['new_homes'] !== 'exclude') {
            delete (updatedQuery as Partial<typeof updatedQuery>)['new_homes'];
        }

        if (
            formValue['property_sub_type'].length === 1 &&
            formValue['property_sub_type'][0] === 'bungalow'
        ) {
            updatedQuery['search-path'] = getSearchPath(
                'bungalow',
                updatedQuery['search-path']
            );
        }

        if (formValue['property_type'] !== 'farms_land') {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'property_type'
            ];
        }

        Object.keys(updatedQuery).forEach((key) => {
            if (updatedQuery[key as keyof typeof updatedQuery] === '') {
                delete updatedQuery[key as keyof typeof updatedQuery];
            }
        });

        await push({
            pathname: updatedPathname,
            query: {
                ...updatedQuery
            }
        });

        setIsLoading(false);

        onUpdate();
    };

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Stack
                alignItems="center"
                flexDirection="row"
                justifyContent="space-between"
                {...{
                    pl: '$S500',
                    pr: '$S200',
                    py: '$S200'
                }}
            >
                <Button
                    category="tertiary"
                    onClick={() => {
                        trackFilterClick();
                        updateFormValue(getDefaultForSaleFilters());
                    }}
                >
                    Reset
                </Button>
                <SectionTitleSmall>Filter your results</SectionTitleSmall>
                <IconButton
                    category="tertiary"
                    label="close"
                    onClick={() => {
                        onClose();
                    }}
                >
                    <CloseMedium />
                </IconButton>
            </Stack>
            <Divider direction="horizontal" />
            <Stack
                flexDirection="column"
                flexWrap="nowrap"
                rowGap="$S500"
                flexGrow="1"
                {...{
                    overflowY: 'auto'
                }}
            >
                <Box width="100%" bpM100={{ px: 'calc($L400 - $S400)' }}>
                    <Accordion
                        value={accordionValue}
                        panels={[
                            {
                                label: 'Bedrooms',
                                value: 'bedrooms',
                                component: (
                                    <Bedrooms
                                        value={{
                                            bedsMin:
                                                formValue.beds_min as BedsMin,
                                            bedsMax:
                                                formValue.beds_max as BedsMax
                                        }}
                                        onChange={({ bedsMin, bedsMax }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                beds_min: bedsMin,
                                                beds_max: bedsMax
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Price',
                                value: 'price',
                                component: (
                                    <Price
                                        value={{
                                            priceMin:
                                                formValue.price_min as PriceMin,
                                            priceMax:
                                                formValue.price_max as PriceMax,
                                            priceFrequency:
                                                formValue.price_frequency as PriceFrequency
                                        }}
                                        onChange={({
                                            priceMin,
                                            priceMax,
                                            priceFrequency
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                price_min: priceMin,
                                                price_max: priceMax,
                                                price_frequency: priceFrequency
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Property type',
                                value: 'property_type',
                                component: (
                                    <PropertyType
                                        value={{
                                            propertyType:
                                                formValue.property_type as PropertyType,
                                            propertySubType:
                                                formValue.property_sub_type as PropertySubType
                                        }}
                                        onChange={({
                                            propertySubType,
                                            propertyType
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                property_sub_type:
                                                    propertySubType,
                                                property_type: propertyType
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Include, exclude & show only',
                                value: 'home_type',
                                component: (
                                    <HomeType
                                        value={{
                                            newHomes:
                                                formValue.new_homes as NewHomes,
                                            isRetirementHome:
                                                formValue.is_retirement_home as IsRetirementHome,
                                            isSharedOwnership:
                                                formValue.is_shared_ownership as IsSharedOwnership,
                                            isAuction:
                                                formValue.is_auction as IsAuction
                                        }}
                                        onChange={({
                                            newHomes,
                                            isRetirementHome,
                                            isSharedOwnership,
                                            isAuction
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                new_homes: newHomes,
                                                is_retirement_home:
                                                    isRetirementHome,
                                                is_shared_ownership:
                                                    isSharedOwnership,
                                                is_auction: isAuction
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Must-haves',
                                value: 'must_haves',
                                component: (
                                    <Features
                                        value={formValue.feature as Features}
                                        onChange={(feature) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                feature: feature
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Property status',
                                value: 'property_status',
                                component: (
                                    <PropertyStatus
                                        value={{
                                            buyerIncentive:
                                                formValue.buyer_incentive as BuyerIncentive,
                                            chainFree:
                                                formValue.chain_free as ChainFree,
                                            reducedPrice:
                                                formValue.reduced_price_only as ReducedPrice,
                                            includeSold:
                                                formValue.include_sold as IncludeSold
                                        }}
                                        onChange={({
                                            buyerIncentive,
                                            chainFree,
                                            reducedPrice,
                                            includeSold
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                buyer_incentive: buyerIncentive,
                                                chain_free: chainFree,
                                                reduced_price_only:
                                                    reducedPrice,
                                                include_sold: includeSold
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Added to site',
                                value: 'added',
                                component: (
                                    <AddedToSite
                                        value={formValue.added as Added}
                                        onChange={(added: Added) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                added: added
                                            }));
                                        }}
                                    />
                                )
                            }
                        ]}
                        onValueChange={updateAccordionValue}
                        maxWidth="100%"
                    />
                </Box>
                <Box px="$S400" pb="$L100" bpM100={{ px: '$L400' }}>
                    <Keywords
                        value={formValue.keywords as Keywords}
                        onChange={(keywords) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                keywords: keywords
                            }));
                        }}
                    />
                </Box>
            </Stack>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                px="$S400"
                py="$S400"
                {...{
                    boxShadow: '$200',
                    zIndex: '$dropdown'
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    bpM100={{ px: 'calc($L400 - $S400)' }}
                >
                    <Button
                        disabled={isLoading}
                        category="primary"
                        onClick={onSubmit}
                    >
                        Update Results
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
